import FirstRoundLetterComp from "components/FirstRoundLetter/FirstRoundLetter";
import Status from "components/Status/Status";
import AssigneeTooltip from "components/Tooltip/AssigneeTooltip";
import TaskActionTooltip from "components/Tooltip/TaskActionTooltip";
import { CommImageNameComp } from "custom_modules/Members/CommonComponents";
import moment from "moment-timezone";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableItem } from "styledComponents/ProjectsNew";
import {
  AssigneeRoundImage,
  AssigneeRoundImageSection,
  RoundPersonImage,
} from "styledComponents/createProject";
import {
  ApprovalTaskListTableHeaderContainer,
  ApprovalTaskListTableRowContainer,
} from "styledComponents/table";

import avatar5 from "../../../assets/img/avatar_5.svg";
import EyeIcon from "../../../assets/img/icons/Eye_normal.svg";
import ComponentCircleLoader from "../../../components/Loaders/ComponentCircleLoader";
import NoDataComponent from "../../../components/NoDataComponent/NoDataComponent";
import { ColoredButton, WhiteButton } from "../../../styledComponents/buttons";
import {
  AssignListText,
  CommonFlex,
  CommonFlexIcon,
  CommonImage,
  CommonText,
  Container,
  ShowMoreAssigneeList,
  TableText,
} from "../../../styledComponents/common";
import { formatTimeInTimezone, globalDateFormat } from "../../../utils/helper";
import StaticTaskLocationModal from "../../ClientsNew/ClientDetails/StaticTaskLocationModal";
import { IconButtonHolder } from "../tasksStyles";
import ApprovalRightpanel from "./ApprovalRightpanel";
import EvidencesModal from "./EvidencesModal";
import MultiAudioPlayerModal from "./MultiAudioPlayer";
import SupervisorModal from "./SupervisorModal";
import { ApproveRejectButton } from "./jobStyles";

const ApprovalTaskListNew = ({
  history,
  handleClearSearch,
  handleSearchByName,
  onSearchTermChange,
  searchTerm,
  isLoading,
  jobList,
  updateTaskStatusRequest,
  selectedTab,
  selectedOrganization,
  getJobDetails,
  jobDetailsLoading,
  jobDetails,
}) => {
  const { t, i18n } = useTranslation();
  const role = selectedOrganization?.role;

  // Dynamic labels based on organization type (default to "operation" if not defined)
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";
  const itemLabel = isOperationType ? t("task") : t("job");
  const itemProgressLabel = isOperationType
    ? t("task_progress")
    : t("job_progress");
  const noSubmittedItemsLabel = isOperationType
    ? t("no_submitted_tasks_found")
    : t("no_submitted_jobs_found");
  const noRejectedItemsLabel = isOperationType
    ? t("no_rejected_tasks_found")
    : t("no_rejected_jobs_found");
  const noApprovedItemsLabel = isOperationType
    ? t("no_approved_tasks_found")
    : t("no_approved_jobs_found");
  const noItemsLabel = isOperationType ? t("no_tasks_found") : t("no_jobs_found");

  const [jobsList, setJobsList] = useState([]);

  const [supervisorNote, setSupervisorNote] = useState("");
  const [staticLocationModalOpen, setStaticLocationModalOpen] = useState(false);
  const [selectedStaticTaskLocation, setSelectedStaticTaskLocation] =
    useState(null);
  const [selectedNoteCard, setSelectedNoteCard] = useState(null);

  //Image Modal
  const [popupOpen, setPopupOpen] = useState(false);
  const [imageEvidences, setImageEvidences] = useState(null);

  //Audio Modal
  const [popupOpenAudio, setPopupOpenAudio] = useState(false);
  const [audioEvidences, setAudioEvidences] = useState(null);

  // Supervisor Note Modal
  const [supervisorNoteModalOpen, setSupervisorNoteModalOpen] = useState(false);
  const [rejectedJobInfo, setRejectedJobInfo] = useState(null);

  // Assignee
  const [moreAssignee, setMoreAssignee] = useState(false);
  const [selectedRow, setSelectedRow] = useState(-1);
  const moreRef1 = useRef(null);
  const moreRef2 = useRef(null);
  const [visibility, setVisibility] = useState(0);

  // ToolTip
  const [selectIndex, setSelectIndex] = useState(-1);
  const [toolTip, setToolTip] = useState(-1);

  // Open Right Sliding Panel
  const [previewPanel, setPreviewPanel] = useState(false);

  // Get organization timezone for time display
  const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();

  // Helper function to parse and format time in organization timezone
  const formatTimeInOrgTimezone = timeString =>
    formatTimeInTimezone(timeString, orgTimezone);

  // Process jobList data similar to old component
  useEffect(() => {
    if (jobList && jobList.length > 0) {
      const processedJobs = jobList.map(item => {
        const assigneesData = [...item.assignees];

        assigneesData.forEach((assignee, index) => {
          const checkedin = item.checkinout?.find(
            obj => obj.event === "checkin" && obj.user === assignee.id,
          );
          const checkedout = item.checkinout?.find(
            obj => obj.event === "checkout" && obj.user === assignee.id,
          );
          const evidence = item.evidences
            ?.filter(obj => obj.created_by === assignee.id)
            .reverse();

          if (checkedin) {
            assigneesData[index].checkedin = checkedin;
          }
          if (checkedout) {
            assigneesData[index].checkedout = checkedout;
          }

          // Determine status
          if (assignee.assignee_status) {
            if (checkedin && checkedout) {
              assigneesData[index].status = "Completed";
            }
          } else {
            if (checkedin) {
              if (!checkedout) {
                assigneesData[index].status = "In Progress";
              }
            } else {
              if (
                !checkedout &&
                moment().unix() < moment(item.required_end_date).unix()
              ) {
                assigneesData[index].status = "To Do";
              } else {
                assigneesData[index].status = "Missed";
              }
            }
          }

          if (evidence) {
            assigneesData[index].evidence = evidence;
          }
        });

        return { ...item, assignees: assigneesData };
      });
      setJobsList(processedJobs);
    } else {
      setJobsList([]);
    }
  }, [jobList]);

  const showMoreAssignee = index => {
    setMoreAssignee(true);
    setSelectedRow(index);
  };

  const visibilityChange = state => {
    setVisibility(state);
  };

  // Handle outside click to close assignee list
  function useOutsideClickHandler(moreRef1, moreRef2) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (
          moreRef1.current &&
          !moreRef1.current.contains(event.target) &&
          moreRef2.current &&
          !moreRef2.current.contains(event.target)
        ) {
          setMoreAssignee(false);
          visibilityChange(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [moreRef1, moreRef2]);
  }
  useOutsideClickHandler(moreRef1, moreRef2);

  const selectedCard = id => {
    if (id !== selectedNoteCard) {
      setSupervisorNote("");
      setSelectedNoteCard(id);
    }
  };

  const onSupervisorNoteChange = e => {
    const { value } = e.target;
    setSupervisorNote(value);
  };

  const getApprovalStatusText = approval => {
    if (!approval) return "-";
    if (approval.status === "pending") return t("pending");
    if (approval.status === "approved") return t("approved");
    if (approval.status === "rejected") return t("rejected");
    return "-";
  };

  const handleRejectButton = job => {
    setRejectedJobInfo({
      id: job.id,
      approval: job.approval,
      type: "rejected",
    });
    setSupervisorNoteModalOpen(true);
  };

  // Wrapper function to handle approval/reject and close panel
  const handleUpdateTaskStatus = (
    task_id,
    approval,
    status,
    supervisorNote,
  ) => {
    updateTaskStatusRequest(task_id, approval, status, supervisorNote);
    // Close the preview panel after approval/reject
    if (previewPanel) {
      setPreviewPanel(false);
    }
  };

  const handlePreviewPanel = (id, workOrderId) => {
    const payload = {
      job_id: id,
      organization_id: selectedOrganization.id,
      workOrder_id: workOrderId,
    };
    getJobDetails(payload);
    setPreviewPanel(true);
  };

  return (
    <Container marginTop="5px" showBorder={true}>
      {imageEvidences?.length > 0 && (
        <EvidencesModal
          isOpen={popupOpen}
          toggle={() => setPopupOpen(false)}
          evidences={imageEvidences}
          selectedEvidence={imageEvidences?.length > 0 && 0}
        />
      )}
      {audioEvidences?.length > 0 && (
        <MultiAudioPlayerModal
          isOpen={popupOpenAudio}
          toggle={() => setPopupOpenAudio(false)}
          audioEvidences={audioEvidences}
        />
      )}
      <ApprovalRightpanel
        isOpen={previewPanel}
        toggle={() => setPreviewPanel(false)}
        isLoading={jobDetailsLoading}
        jobDetails={jobDetails}
        timezone={selectedOrganization?.timezone}
        setSupervisorNoteModalOpen={setSupervisorNoteModalOpen}
        selectedOrganization={selectedOrganization}
        updateTaskStatusRequest={handleUpdateTaskStatus}
        setRejectedJobInfo={setRejectedJobInfo}
      />
      <SupervisorModal
        isOpen={supervisorNoteModalOpen}
        toggle={() => {
          setSupervisorNoteModalOpen(false);
          setRejectedJobInfo(null);
        }}
        isLoading={isLoading}
        rejectedJobInfo={rejectedJobInfo}
        updateTaskStatusRequest={handleUpdateTaskStatus}
      />
      <StaticTaskLocationModal
        isOpen={staticLocationModalOpen}
        toggle={() => {
          setStaticLocationModalOpen(false);
          setSelectedStaticTaskLocation(null);
        }}
        taskLocation={selectedStaticTaskLocation}
      />
      {/* Loading Component */}
      {isLoading && <ComponentCircleLoader />}

      {/* No Data Component */}
      {!isLoading && jobList?.length === 0 && (
        <NoDataComponent
          title={
            selectedTab === "submitted"
              ? isOperationType ? t("no_submitted_tasks_found") : t("no_submitted_jobs_found")
              : selectedTab === "rejected"
              ? isOperationType ? t("no_rejected_tasks_found") : t("no_rejected_jobs_found")
              : selectedTab === "approved"
              ? isOperationType ? t("no_approved_tasks_found") : t("no_approved_jobs_found")
              : isOperationType ? t("no_tasks_found") : t("no_jobs_found")
          }
        />
      )}

      {/* Job List */}
      {isLoading ? (
        <ComponentCircleLoader />
      ) : (
        <>
          {/* Table Header Section */}
          {jobList && jobList.length > 0 && (
            <ApprovalTaskListTableHeaderContainer orgType={orgType}>
              <TableItem>
                <TableText>{isOperationType ? t("task_name") : t("job_name")}</TableText>
              </TableItem>
              {isOperationType && (
                <TableItem>
                  <TableText>{t("project")}</TableText>
                </TableItem>
              )}

              <TableItem>
                <TableText>{t("date_time")}</TableText>
              </TableItem>
              {orgType === "service" && (
                <TableItem>
                  <TableText>{t("customer")}</TableText>
                </TableItem>
              )}

              <TableItem>
                <TableText>{t("assignee")}</TableText>
              </TableItem>
              {(role === "owner" || role === "admin") && (
                <TableItem>
                  <TableText>{t("status")}</TableText>
                </TableItem>
              )}
              {/* {selectedTab === "submitted" &&
              localStorage.getItem("selected_organization_role") !==
                "employee" &&
              !localStorage.getItem("viewer_role") ? ( */}
              <TableItem>
                <TableText>{t("actions")}</TableText>
              </TableItem>
              {/* ) : null} */}
            </ApprovalTaskListTableHeaderContainer>
          )}

          {/* table row Body Section */}
          {jobsList &&
            jobsList.length > 0 &&
            jobsList.map((job, jobIndex) => (
              <ApprovalTaskListTableRowContainer key={job.id} orgType={orgType}>
                <TableItem>
                  <TableText>{job.name}</TableText>
                </TableItem>
                {isOperationType && <TableItem>
                  <TableText
                    name
                    onClick={() =>
                      history.push(
                        `/user/details-${
                          orgType === "service" ? "job" : "project"
                        }?id=${job.work_order?.id}`,
                      )
                    }
                    style={{ cursor: "pointer" }}
                  >
                    {job.work_order?.name || "-"}
                  </TableText>
                </TableItem>}
                <TableItem>
                  <TableText>{globalDateFormat(job.start_time,orgTimezone)}</TableText>
                  <TableText name>
                    {formatTimeInOrgTimezone(job.start_time,)} -{" "}
                    {formatTimeInOrgTimezone(job.end_time)}
                  </TableText>
                </TableItem>
                {orgType === "service" && (
                  <TableItem>
                    <div
                      onClick={() =>
                        job.work_order?.client_id &&
                        history.push(
                          `/user/customer-details?id=${job.work_order.client_id}`,
                        )
                      }
                      style={{
                        cursor: job.work_order?.client_id
                          ? "pointer"
                          : "default",
                      }}
                    >
                      <CommImageNameComp
                        imgSource={job?.work_order?.client_image || avatar5}
                        name={job?.work_order?.client_name || "-"}
                        size="40px"
                        fontSize="13px"
                        index={2}
                        color={job?.work_order?.client_color}
                        hoverUnderline={!!job.work_order?.client_id}
                        showAddDesignation={false}
                        designation={job?.work_order?.client_designation}
                        //   email={job?.work_order?.client_email}
                      />
                    </div>
                  </TableItem>
                )}
                <TableItem>
                  <AssigneeRoundImageSection
                    position={job.assignees.length}
                    style={{ marginRight: `auto` }}
                  >
                    {job.assignees && job.assignees.length > 3 && (
                      <AssigneeRoundImage
                        index="more"
                        onClick={() => {
                          showMoreAssignee(jobIndex);
                          visibilityChange(true);
                        }}
                        ref={moreRef2}
                        style={{
                          border: `1px solid #fff`,
                          paddingLeft: "3px",
                        }}
                      >
                        {`+${job.assignees.length - 3}`}
                      </AssigneeRoundImage>
                    )}
                    <>
                      {job?.assignees?.length > 0 ? (
                        <>
                          {job.assignees
                            .filter((person, index) => index < 3)
                            .map((filteredPerson, index) => (
                              <AssigneeRoundImage
                                key={index}
                                index={
                                  job.assignees.length < 4
                                    ? index + 1
                                    : index + 2
                                }
                              >
                                <AssigneeTooltip
                                  toolTipText={`${filteredPerson.first_name} ${filteredPerson.last_name}`}
                                />

                                {filteredPerson.avatar ? (
                                  <RoundPersonImage
                                    src={
                                      filteredPerson.avatar
                                        ? filteredPerson.avatar
                                        : avatar5
                                    }
                                    alt=""
                                  />
                                ) : (
                                  <FirstRoundLetterComp
                                    text={`${filteredPerson.first_name} ${filteredPerson.last_name}`}
                                    backColor={index}
                                    color={filteredPerson.color}
                                  />
                                )}
                              </AssigneeRoundImage>
                            ))}
                          {moreAssignee && selectedRow === jobIndex && (
                            <ShowMoreAssigneeList ref={moreRef1}>
                              {job.assignees.map((member, index) => (
                                <Fragment key={index}>
                                  <AssigneeRoundImage index={0} size="30px">
                                    {member.avatar ? (
                                      <RoundPersonImage
                                        src={
                                          member.avatar
                                            ? member.avatar
                                            : avatar5
                                        }
                                        alt=""
                                        size="30px"
                                        borderColor="#c2cce1"
                                      />
                                    ) : (
                                      <FirstRoundLetterComp
                                        text={`${member.first_name} ${member.last_name}`}
                                        backColor={index}
                                        color={member.color}
                                      />
                                    )}
                                  </AssigneeRoundImage>
                                  <AssignListText>
                                    {`${member.first_name} ${member.last_name}`}
                                  </AssignListText>
                                </Fragment>
                              ))}
                            </ShowMoreAssigneeList>
                          )}
                        </>
                      ) : (
                        <CommonText margin="10px 18px">-</CommonText>
                      )}
                    </>
                  </AssigneeRoundImageSection>
                </TableItem>
                {/* Approval Status Column */}
               {(role === "owner" || role === "admin") && <TableItem>
                  {/* submit */}
                  {selectedTab === "submitted"  && (
                    <CommonFlex disableResponsive={true} gap="10px">
                      <ApproveRejectButton
                        type="reject"
                        onClick={() => handleRejectButton(job)}
                      >
                        Reject
                      </ApproveRejectButton>
                      <ApproveRejectButton
                        type="approve"
                        onClick={() =>
                          updateTaskStatusRequest(
                            job.id,
                            job.approval,
                            "approved",
                            "",
                          )
                        }
                      >
                        Approve
                      </ApproveRejectButton>
                    </CommonFlex>
                  )}
                  {/* approved */}
                  {selectedTab === "approved" && (
                    <CommonFlex gap="10px">
                      <Status
                        status={job.approval?.status?.toLowerCase()}
                        showDot={true}
                      />
                    </CommonFlex>
                  )}

                  {/* rejected */}
                  {selectedTab === "rejected" && (
                    <CommonFlex gap="10px">
                      <Status
                        status={job.approval?.status?.toLowerCase()}
                        showDot={true}
                      />
                    </CommonFlex>
                  )}
                </TableItem>}

                {/* action section */}
                <CommonFlexIcon gap="0px">
                  <IconButtonHolder
                    onClick={() =>
                      handlePreviewPanel(job.id, job.work_order.id)
                    }
                  >
                    <CommonImage src={EyeIcon} alt="details" size="24px" />
                    <TaskActionTooltip toolTipText={t(isOperationType ? "preview_task" : "preview_job")} />
                  </IconButtonHolder>
                </CommonFlexIcon>
              </ApprovalTaskListTableRowContainer>
            ))}
        </>
      )}
    </Container>
  );
};

export default ApprovalTaskListNew;
